import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { ThemeContext } from '../../index';
import { Layout, theme, Button, Typography } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import Sidebar from './common/Sidebar';
import PublicHeader, { PUBLIC_HEADER_HEIGHT } from './common/PublicHeader';
import FeedbackButton from '../system/Feedback.component';
import OrganizationSwitcher from '../organization/OrganizationSwitcher.component';
import DataAgreementNotification from '../dataManagement/DataAgreementNotification';
import Breadcrumb from '../common/Breadcrumb';
import uoeLogo from '../../assets/images/UOE.png';
import { getUserPermissions } from '../../actions/userActions';
import {
  EMPTY_NAV_POLICY_CONFIG,
  getPagePoliciesForPath,
  loadNavPolicyConfig,
  subscribeNavPolicyConfig,
} from '../../utils/navPolicyConfig';
import { NAV_POLICY_EXEMPT_PATHS } from '../../config/navPolicyCatalog';
import { hasAnyPermission, isSuperUser } from '../../utils/permissionUtils';
import IdleSessionPrompt from '../auth/IdleSessionPrompt';
import { performSessionLogout } from '../../utils/sessionLogout';
import { useIdleSession } from '../../hooks/useIdleSession';
import {
  resolveIdleSessionConfig,
} from '../../utils/idleSessionUtils';
const { Content, Footer } = Layout;
const { Text, Link } = Typography;

const AuthLayout = ({ content, title }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { themeConfig } = useContext(ThemeContext);
  const { algorithm } = themeConfig;
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [navPolicies, setNavPolicies] = useState(EMPTY_NAV_POLICY_CONFIG);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);

  const {
    token: { colorBgContainer, colorTextSecondary, colorBorder },
  } = theme.useToken();

  const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo')), []);
  const pageTitle = title
    ? `${title} - OpenEP`
    : 'OpenEP';
  const { idleTimeoutMs, warningBeforeMs } = useMemo(() => resolveIdleSessionConfig(), []);
  const idleSession = useIdleSession({
    enabled: !!userInfo,
    idleTimeoutMs,
    warningBeforeMs,
    pageTitle,
  });

  const idleSessionUi = (
    <>
      {!idleSession.warningOpen && (
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
      )}
      <IdleSessionPrompt {...idleSession} />
    </>
  );

  // Authentication check
  useEffect(() => {
    if (!userInfo) {
      performSessionLogout();
    }
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo) return;
    dispatch(getUserPermissions()).catch(() => {});
  }, [dispatch, userInfo]);

  useEffect(() => {
    let active = true;

    loadNavPolicyConfig()
      .then((config) => {
        if (active) {
          setNavPolicies(config);
          setPoliciesLoaded(true);
        }
      })
      .catch(() => {
        if (active) {
          setNavPolicies(EMPTY_NAV_POLICY_CONFIG);
          setPoliciesLoaded(true);
        }
      });

    const unsubscribe = subscribeNavPolicyConfig((config) => {
      if (active) setNavPolicies(config);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  // Responsive handlers
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle handler for sidebar (desktop only)
  const handleToggleSider = () => {
    setCollapsed(!collapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
  };

  if (!policiesLoaded) {
    return idleSessionUi;
  }

  if (
    !isSuperUser()
    && !NAV_POLICY_EXEMPT_PATHS.has(location.pathname)
  ) {
    const requiredPolicies = getPagePoliciesForPath(navPolicies, location.pathname);
    if (requiredPolicies && !hasAnyPermission(requiredPolicies)) {
      return (
        <>
          {idleSessionUi}
          <Navigate to="/unauthorized" replace state={{ from: location }} />
        </>
      );
    }
  }

  return (
    <>
      {idleSessionUi}
      <Layout style={{ minHeight: '100vh', background: colorBgContainer }}>
        <PublicHeader
          fixed
          leading={isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={toggleMobileMenu}
              style={{ color: '#FFFFFF', padding: '4px 8px' }}
            />
          ) : null}
        />
        <Sidebar
          navPolicies={navPolicies}
          collapsed={isMobile ? false : collapsed}
          isMobile={isMobile}
          visible={mobileMenuVisible}
          onClose={() => setMobileMenuVisible(false)}
          onToggleCollapse={handleToggleSider}
          algorithm={algorithm}
          userRole={userInfo?.roles?.includes('SuperUser') ? 'superuser' : 'user'}
        />
        
        <Layout style={{
          marginLeft: isMobile ? 0 : (collapsed ? 48 : 130),
          transition: 'all 0.2s',
          background: colorBgContainer,
          position: 'relative'
        }}>
          <Content style={{ 
            background: colorBgContainer,
            minHeight: 700,
            position: 'relative',
            zIndex: 1,
            paddingTop: PUBLIC_HEADER_HEIGHT
          }}>
            <div style={{ 
              padding: '0 24px 8px 24px', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              <Breadcrumb />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '15px'
              }}>
                <OrganizationSwitcher />
                <DataAgreementNotification userInfo={userInfo} />
              </div>
            </div>
            {content}
          </Content>

          <Footer style={{ 
            textAlign: 'center', 
            color: colorTextSecondary,
            background: colorBgContainer,
            borderTop: `1px solid ${colorBorder}`,
            padding: '24px 12px'
          }}>
            <img
              src={uoeLogo}
              alt="The University of Edinburgh"
              style={{ height: 48, width: 'auto', marginBottom: 16, display: 'inline-block' }}
            />
            <div style={{ marginBottom: '12px' }}>
              {t('OpenEP ©{{year}} All Rights Reserved.', { year: new Date().getFullYear() })}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Some account and system information may be sent to OpenEP. We will use it to fix problems and improve our services, subject to our{' '}
              <Link href="https://aro.tech/privacy-policy">Privacy Policy</Link> and{' '}
              <Link href="https://aro.tech/terms">Terms of Service</Link>. We may email you for more information or updates.{' '}
              <Link href="https://aro.tech/legal-help">Go to Legal Help</Link> to ask for content changes for legal reasons.
            </Text>
          </Footer>

          <FeedbackButton />
        </Layout>
      </Layout>
    </>
  );
};

export default AuthLayout;